/**
 * Shared Patch Notes Data
 * Used by both PatchNotesModal (top right) and MyAgentsSection (agent cards)
 */

import { Bot, FileText, Wrench, Package, Scissors, FolderOpen, Sparkles, Settings } from 'lucide-react';
import type { ComponentType } from 'react';

// ============================================
// FEATURE FLAGS
// ============================================

/**
 * Enable/disable version tracking and notification indicators
 * When false:
 * - No "NEW" badges on agent cards
 * - No notification dots on version badges
 * - No user-viewed tracking for version modals
 * Set to true to enable the version tracking system
 */
export const ENABLE_VERSION_TRACKING = true;

/**
 * Baseline date for version tracking (ISO date string, YYYY-MM-DD).
 *
 * Only agents whose `lastUpdated` is AFTER this date will trigger NEW/UPDATED
 * badges. This prevents a flood of badges on initial deploy — existing agents
 * have `lastUpdated` in the past so they're silently grandfathered in. The
 * first real version bump after this date will surface the pip naturally.
 *
 * Set this to the deploy date of the version-tracking feature.
 */
export const VERSION_TRACKING_BASELINE_DATE = '2026-04-20';

// ============================================

// Current application version
export const APP_VERSION = '1.0.2';

export interface PatchNoteEntry {
  version: string;
  date: string;
  highlights: string[];
  // Full markdown content for expanded view (optional)
  content?: string;
}

export interface AgentPatchNotesConfig {
  agentId: string;
  displayName: string;
  icon: ComponentType<{ className?: string }>;
  colors: { from: string; to: string };
  notes: PatchNoteEntry[];
}

// UniFirst brand palette — each agent gets a unique color pair drawn from
// the official primary, secondary, and tertiary swatches (Brand Standards Manual).
export const AGENT_COLORS: Record<string, { from: string; to: string }> = {
  'platform':          { from: '#97999B', to: '#63666A' }, // Slate Gray → Cool Gray 10
  'aldo':              { from: '#00B194', to: '#007161' }, // PMS 339 → Deep Green
  'hrpolicy':          { from: '#007672', to: '#005a4d' }, // PMS 7718 Teal
  'hr-policy':         { from: '#007672', to: '#005a4d' },
  'pwms-support':      { from: '#63666A', to: '#4a4d50' }, // Cool Gray 10
  'pwms':              { from: '#63666A', to: '#4a4d50' },
  'product':           { from: '#DAAA00', to: '#b08900' }, // PMS 110 Burnt Orange
  'direct_embroidery': { from: '#5a3a3b', to: '#3F2021' }, // Dark Crimson
  'unifirst_docs':     { from: '#A8AD00', to: '#7a7e00' }, // PMS 383 Olive
  'orientation':       { from: '#CEDC00', to: '#A8AD00' }, // PMS 381 Bright Lime → Olive
};

export const AGENT_ICONS: Record<string, {
  icon: ComponentType<{ className?: string }>;
  colors: { from: string; to: string };
}> = {
  'platform':          { icon: Settings,   colors: AGENT_COLORS['platform'] },
  'aldo':              { icon: Bot,        colors: AGENT_COLORS['aldo'] },
  'hrpolicy':          { icon: FileText,   colors: AGENT_COLORS['hrpolicy'] },
  'hr-policy':         { icon: FileText,   colors: AGENT_COLORS['hr-policy'] },
  'pwms-support':      { icon: Wrench,     colors: AGENT_COLORS['pwms-support'] },
  'pwms':              { icon: Wrench,     colors: AGENT_COLORS['pwms'] },
  'product':           { icon: Package,    colors: AGENT_COLORS['product'] },
  'direct_embroidery': { icon: Scissors,   colors: AGENT_COLORS['direct_embroidery'] },
  'unifirst_docs':     { icon: FolderOpen, colors: AGENT_COLORS['unifirst_docs'] },
  'orientation':       { icon: Sparkles,   colors: AGENT_COLORS['orientation'] },
};

// Platform-level patch notes
export const PLATFORM_PATCH_NOTES: PatchNoteEntry[] = [
  {
    version: `${APP_VERSION}`,
    date: '2025-12',
    highlights: [
      'Concierge Mode: Scout intelligently routes to specialist agents via standard chat',
      'Seamless agent handoffs without losing context',
      'Use / or @ to mention a specific agent',
      'My Agents section to see all available agents on home page'
    ],
    content: `### Concierge Mode Launch 

- **Smart Agent Routing**: Scout now intelligently routes your questions to the most appropriate specialist agent
- **Seamless Handoffs**: Conversations flow naturally between agents without losing context
- **Improved Response Quality**: Better understanding of complex, multi-topic questions
- **Maintained Control**: You can still use the @ or / to mention a specific agent, or use the My Agents section to see all available agents on home page and chat with one in isolation.
`
  },
];

// ============================================
// Per-agent patch notes
// ============================================
// IMPORTANT: Version numbers here should match the "version" field in:
//   apps/express-api/config/agents.json
// 
// agents.json controls the version badge on agent cards
// This file controls the changelog/patch notes in version modals
// ============================================
export const AGENT_PATCH_NOTES: Record<string, PatchNoteEntry[]> = {
  'orientation': [
    {
      version: '1.0.0',
      date: '2025-10',
      highlights: [
        'Initial release',
        'Company history and culture, benefits overview',
        'First-day setup guidance',
        'System access help (ADP, UKG, PolicyTech, etc.)'
      ]
    }
  ],
  'hrpolicy': [
    {
      version: '1.0.0',
      date: '2025-11',
      highlights: [
        'Initial release',
        'Covers basic set of policies for all employees from Time Off to Dress Code',
        'Citation links directly to PolicyTech documents'
      ],
      content: `### HR Policy Agent Updates

- **Enhanced Policy Search**: Improved search accuracy for company policies
- **State-Specific Guidance**: Better handling of state-specific HR policies
- **Citation Improvements**: More precise source references in responses`
    },
  ],
  'pwms-support': [
    {
      version: '1.1.0',
      date: '2024-11',
      highlights: [
        'Design workflow guidance',
        'Improved troubleshooting',
        'Role-based responses'
      ],
      content: `### PWMS Support Agent Updates

- **Design Workflow Guidance**: Better assistance with PWMS design creation
- **Troubleshooting Help**: Improved problem resolution for common PWMS issues
- **Role-Based Answers**: Responses now account for different user roles (Sales Rep, Admin, etc.)`
    }
  ],
  'product': [
    {
      version: '1.0.0',
      date: '2026-05',
      highlights: [
        'Promoted to production — now a default agent for all UniFirst users',
        'Comprehensive in-app User Guide with visual badge reference',
        'Embedding pipeline upgraded to text-embedding-3-large',
        'Multi-intent splitting for cross-product queries',
        'Refreshed welcome message with project contact and data-freshness disclaimer'
      ],
      content: `### Product Searchlight v1.0 — General Availability 🚀

Product Searchlight is now generally available to all UniFirst users.

**What's new:**
- **General Availability**: Promoted from UAT to production. Visible by default for every user.
- **In-app User Guide**: Open the Settings cog → "User Guide" for a complete tags & data-fields reference, rendered with real visual badges that match what you see on cards.
- **Better industry detection**: Embedding pipeline upgraded to \`text-embedding-3-large\` with recalibrated thresholds across the canonicalizer, ranker, and orchestrator.
- **Multi-intent queries**: Asking for two product types in one breath ("shirts and shorts for childcare") now correctly splits into two parallel searches.
- **Refreshed intro**: Data-freshness disclaimer, Facility Services scope note, and project contact at searchlight@unifirst.com.

> ⚠️ **Not intended for Facility Services use at this time.**`
    },
    {
      version: '0.4.1',
      date: '2025-06',
      highlights: [
        'Improved search accuracy',
        'Better product comparisons'
      ]
    },
    {
      version: '0.4.0',
      date: '2024-12',
      highlights: [
        'Product catalog search',
        'Smart filtering by features',
        'Visual product cards',
        'Safety equipment support'
      ],
      content: `### Product Agent Launch 🆕

- **Product Catalog Search**: Find uniforms, apparel, and safety equipment quickly
- **Smart Filtering**: Filter by size, color, material, price, and safety ratings
- **Visual Product Cards**: See product images and details at a glance
- **Comparison Support**: Compare similar products side-by-side`
    }
  ],
  'direct_embroidery': [
    {
      version: '1.0.0',
      date: '2024-10',
      highlights: [
        'Machine operation guides',
        'Hooping and threading help',
        'Troubleshooting support'
      ]
    }
  ],
  'unifirst_docs': [
    {
      version: '1.0.0',
      date: '2026-01',
      highlights: [
        'Initial release',
        'Comprehensive process & procedure guides',
        'Software help (TRACS, ABS, Oracle, Route Assistant)',
        'Forms, templates & protocols'
      ]
    }
  ]
};

// Helper to get the latest version for an agent
export const getLatestVersion = (agentId: string): string => {
  if (agentId === 'platform') {
    return PLATFORM_PATCH_NOTES[0]?.version || '1.0.0';
  }
  const notes = AGENT_PATCH_NOTES[agentId];
  return notes?.[0]?.version || '1.0.0';
};

// Helper to get all patch notes for an agent
export const getAgentPatchNotes = (agentId: string): PatchNoteEntry[] => {
  if (agentId === 'platform') {
    return PLATFORM_PATCH_NOTES;
  }
  return AGENT_PATCH_NOTES[agentId] || [];
};

